import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
  SimpleChanges,
} from '@angular/core';
import {
  Task,
  ImplementationTimelineEnum,
  StatusDto,
  TaskStatusEnum,
} from '../../../../core/interfaces';

import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
  changeTaskStatus,
  changeTaskStatus_Failure,
  changeTaskStatus_Success,
  fetchTaskDetails,
  markTaskAsReviewed,
  toggleSidebar,
} from '../../store/tasks.actions';
import { UntilDestroy } from '@ngneat/until-destroy';
import { setTitle } from 'projects/shared/src/lib/components/sidebar/store/details-sidebar.actions';
import { DropdownOption } from 'projects/shared/src/lib/core/components/rounded-dropdown/models/dropdown.models';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'app-task-item',
  templateUrl: './task-item.component.html',
  styleUrls: ['./task-item.component.scss'],
  standalone: false,
})
export class TaskItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public itemData!: Task;
  @Input() public isClickable: boolean = true;
  @Input() circleColor?: string;
  @Input() statuses?: StatusDto[];
  @Input() controlId?: string;
  @ViewChild('outputModalBox') outputModalBox!: ElementRef;
  statusDropdownOptions: DropdownOption[] = [];
  statusFormControl!: FormControl;
  status!: string;
  showOutputModal = false;
  outputTextControl: FormControl = new FormControl('', Validators.required);
  private selectedStatus?: number;
  private timeoutId?: any;
  // NgRx actions for dropdown tracking
  changeTaskStatus_Success = changeTaskStatus_Success;
  changeTaskStatus_Failure = changeTaskStatus_Failure;
  changeTaskStatus = changeTaskStatus;

  readonly Object = Object;
  readonly ImplementationTimelineEnum = ImplementationTimelineEnum;
  readonly TaskStatusEnum = TaskStatusEnum;

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.getStatus();
    this.statusDropdownOptions = this.getStatusDropdownOptions();
    this.statusFormControl = this.fb.control(this.getStatusControlValue());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['statuses']) {
      this.statusDropdownOptions = this.getStatusDropdownOptions();
      if (this.statusFormControl) {
        this.statusFormControl.setValue(this.getStatusControlValue());
      }
    }
    if (changes['itemData'] && this.itemData) {
      this.getStatus();
      if (this.statusFormControl) {
        this.statusFormControl.setValue(this.getStatusControlValue());
      }
    }
  }

  get outputPromptLabel(): string {
    const currentLang = this.translateService.currentLang;
    // T180 / M4A: canonical outputPromptEn / He / Ar only.
    if (currentLang?.startsWith('he')) {
      const he = this.itemData.outputPromptHe?.trim();
      if (he) {
        return he;
      }
    }
    if (currentLang?.startsWith('ar')) {
      const ar = this.itemData.outputPromptAr?.trim();
      if (ar) {
        return ar;
      }
    }
    const en = this.itemData.outputPromptEn?.trim();
    if (en) {
      return en;
    }
    return this.translateService.instant('TASKS.OUTPUT_REQUIRED_FALLBACK');
  }

  private getStatus(): void {
    let status = '';
    switch (this.itemData.severity) {
      case 0:
        status = 'low';
        break;
      case 1:
        status = 'medium';
        break;
      case 2:
        status = 'high';
        break;
      case 3:
        status = 'critical';
        break;
    }
    this.status = status;
  }

  private getStatusDropdownOptions(): DropdownOption[] {
    return (
      this.statuses
        ?.filter((status) => status.selectable)
        .map((status) => ({
          value: status.id,
          label: status.name.replace(/([A-Z])/g, ' $1').trim(),
        })) ?? []
    );
  }

  private getStatusControlValue(): {
    value: number;
    label: string;
    highlight: boolean;
    showOrangeBorder: boolean;
  } {
    const selectedStatus = this.statuses?.find(
      (status) => status.id === this.itemData.taskStatusId,
    );
    return {
      value: this.itemData.taskStatusId,
      label: selectedStatus ? selectedStatus.name.replace(/([A-Z])/g, ' $1').trim() : '',
      highlight:
        this.itemData.taskStatusId === TaskStatusEnum.pending &&
        !this.itemData.isBlocked &&
        !this.itemData.isBlockedByPrerequisites &&
        !this.itemData.taskLocked,
      showOrangeBorder:
        !this.itemData.isReviewed &&
        !this.itemData.isBlocked &&
        !this.itemData.isBlockedByPrerequisites &&
        !this.itemData.taskLocked,
    };
  }

  showTaskSidebar(): void {
    this.store.dispatch(toggleSidebar({ showSidebar: true }));
    this.store.dispatch(fetchTaskDetails({ taskId: this.itemData.id }));
    this.store.dispatch(setTitle({ title: this.itemData.name }));
  }

  resetToPreviousStatus = () => {
    // itemData only changes once the request succeeds, so it still holds the
    // status from before the user's selection.
    this.statusFormControl.setValue(this.getStatusControlValue());
  };
  borderChanged = (color: string, task: Task) => {
    if (color === 'red') {
      this.resetToPreviousStatus();
      return;
    }
    if (!task.isReviewed && color === 'green') {
      this.store.dispatch(markTaskAsReviewed({ taskId: task.id }));
    }
  };
  changeStatus(status: number, id: string) {
    if (this.itemData.requiresOutput && status === TaskStatusEnum.completed) {
      this.selectedStatus = status;
      this.resetToPreviousStatus();
      this.showOutputModal = true;
      this.outputTextControl.reset('');
      this.outputTextControl.markAsPristine();
      this.outputTextControl.markAsUntouched();
      // Add click outside listener after modal is rendered
      this.timeoutId = setTimeout(() => {
        document.addEventListener('click', this.handleClickOutsideModal);
      }, 100);
      return;
    }

    this.store.dispatch(
      changeTaskStatus({
        taskId: id,
        status,
        outputText: null,
        controlId: this.controlId,
      }),
    );
  }

  handleClickOutsideModal = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (this.outputModalBox?.nativeElement && !this.outputModalBox.nativeElement.contains(target)) {
      this.cancelOutputModal();
    }
  };

  cancelOutputModal(): void {
    this.selectedStatus = undefined;
    this.showOutputModal = false;
    this.outputTextControl.reset('');
    document.removeEventListener('click', this.handleClickOutsideModal);
  }

  confirmOutputModal(): void {
    if (!this.selectedStatus) {
      return;
    }
    const rawValue = this.outputTextControl.value ?? '';
    const trimmedValue = rawValue.trim();

    if (!trimmedValue.length) {
      this.outputTextControl.markAsTouched();
      this.outputTextControl.setErrors({ required: true });
      return;
    }

    const resetToPreviousStatus = () => {
      const previousStatus = this.statuses?.find(
        (statusDto) => statusDto.id === this.selectedStatus,
      );
      this.statusFormControl.setValue({
        value: this.selectedStatus,
        label: previousStatus ? previousStatus.name.replace(/([A-Z])/g, ' $1').trim() : '',
        highlight: this.itemData.taskStatusId === TaskStatusEnum.pending,
        showOrangeBorder: !this.itemData.isReviewed,
      });
    };

    this.store.dispatch(
      changeTaskStatus({
        taskId: this.itemData.id,
        status: this.selectedStatus,
        outputText: trimmedValue,
        successFnc: resetToPreviousStatus,
        controlId: this.controlId,
      }),
    );

    this.showOutputModal = false;
    document.removeEventListener('click', this.handleClickOutsideModal);
  }

  ngOnDestroy(): void {
    // Clean up event listener if component is destroyed while modal is open
    document.removeEventListener('click', this.handleClickOutsideModal);
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}
